/**
 * THE instructor record. One entry per person, read by:
 *   - /instructors and /instructors/:slug (client/src/pages/instructors/)
 *   - the JSON-LD on those pages and the Person @id every workshop Event and
 *     Course points at (shared/instructor-schema.ts, server/page-meta.ts)
 *   - the VIP 1:1 picker (/vip-session)
 *
 * Research behind every line: .gen-work/instructor-bios/<slug>.md (2026-10-13,
 * repo + company Drive + open web, each fact tagged VERIFIED / COMPANY-CLAIMED
 * / UNCERTAIN). Rules this file keeps:
 *   - Only facts with a source. Numbers that conflict across our own copy are
 *     stated the way the independent source states them (Udemy, PPC Hero).
 *   - Client names appear only where the client is already public on our own
 *     site as an agency client, and never as one person's personal account.
 *   - `CONFIRM:` comments mark what the person still has to sign off. They are
 *     the open list for the bio interviews; delete each one once answered.
 *
 * No em dashes in any string (house copy rule).
 */
import type { Instructor } from "./instructor-types";

export const INSTRUCTORS: Instructor[] = [
  // ─── Isaac Rudansky ─────────────────────────────────────────────────────
  {
    slug: "isaac-rudansky",
    aside: "also a novelist",
    name: "Isaac Rudansky",
    firstName: "Isaac",
    // CONFIRM: Founder vs "Founder, AdVenture Media & The Modern Marketing Institute".
    // Patrick is CEO of AdVenture; schema elsewhere still calls Isaac "CEO, MMI".
    jobTitle: "Founder",
    roleLine: "Founder, AdVenture Media",
    summary:
      "Isaac Rudansky founded AdVenture Media, a New York performance advertising agency, and built one of Udemy's best-selling Google Ads courses. Today he leads AdVenture's live Claude workshops, where marketers, founders and operators build real work with AI in a single session.",
    bio: [
      "Isaac started AdVenture Media in 2012 while finishing a master's degree in industrial and organizational psychology at Hofstra University. The first client was a dental office in Queens. The agency has since grown into a team that runs paid search, paid social and creative for brands including Forbes, AMC Networks and Hanes.",
      "Teaching has run alongside the agency from the start. His beginner's Google Ads tutorial on YouTube passed a million views in 2014, and in 2015 he published a Google Ads course on Udemy that is still one of the platform's best sellers: more than 237,000 students and a 4.7 rating from over 55,000 reviews. Courses on Meta advertising and landing page design followed, along with classes on CreativeLive.",
      "PPC Hero named him to its Top 50 Most Influential PPC Experts in 2020 and its Top 25 in 2021.",
      "In 2026 he turned that teaching habit toward AI. He built AdVenture's Claude workshop series, starting with Claude Code for Beginners, and teaches Claude Cowork and Claude Code live on Zoom. His style is to build in front of the room, show the draft that doesn't work, and fix it with everyone watching.",
      "Isaac is also a novelist. His middle-grade fantasy, Georgie Summers and the Scribes of Scatterplot, was picked up in a three-book deal in 2025.",
    ],
    stats: [
      { value: "237,000+", label: "Students in his flagship Udemy course" },
      { value: "4.7", label: "Udemy rating, 55,000+ reviews" },
      { value: "2012", label: "Founded AdVenture Media" },
      { value: "Top 25", label: "PPC Hero's most influential experts, 2021" },
    ],
    timeline: [
      { year: "2012", text: "Founds AdVenture Media while earning his master's in I/O psychology at Hofstra." },
      { year: "2014", text: "His Google Ads tutorial on YouTube passes one million views." },
      { year: "2015", text: "Publishes his Google Ads course on Udemy, now with 237,000+ students." },
      { year: "2017", text: "Launches courses on Meta advertising and landing page design." },
      { year: "2020", text: "Named to PPC Hero's Top 50 Most Influential PPC Experts." },
      { year: "2021", text: "Named to PPC Hero's Top 25 Most Influential PPC Experts." },
      { year: "2025", text: "Signs a three-book deal for his novel, Georgie Summers and the Scribes of Scatterplot." },
      { year: "2026", text: "Launches AdVenture's live Claude workshops, starting with Claude Code for Beginners." },
    ],
    credentials: ["M.A., Industrial and Organizational Psychology, Hofstra University"],
    alumniOf: ["Hofstra University"],
    knowsAbout: [
      "Claude",
      "Claude Code",
      "Claude Cowork",
      "AI for marketing",
      "Google Ads",
      "Pay-per-click advertising",
      "Meta advertising",
      "Landing page design",
      "Conversion rate optimization",
    ],
    works: [
      {
        kind: "course",
        title: "Ultimate Google Ads Training: Profit with Pay Per Click",
        year: "2015",
        url: "https://www.udemy.com/course/the-ultimate-google-adwords-training-course/",
        note: "Udemy, updated every year",
      },
      { kind: "book", title: "Georgie Summers and the Scribes of Scatterplot", note: "Middle-grade fantasy" },
      {
        kind: "podcast",
        title: "Mislaibeled, episode 87",
        year: "2024",
        url: "https://mislaibeled.buzzsprout.com/1820787/episodes/16128523",
      },
      {
        kind: "podcast",
        title: "WP-Tonic Show #414",
        year: "2019",
        url: "https://wp-tonic-show-a-wordpress-podcast.castos.com/episodes/414-wp-tonic-show-with-special-guest-isaac-rudansky",
      },
      { kind: "article", title: "Author page at Writer's Digest", url: "https://writersdigest.com/author/isaac-rudansky" },
    ],
    workshops: [
      "claude-code-for-beginners-workshop",
      "claude-cowork-for-beginners-workshop",
      "claude-for-design-workshop",
      "the-ai-first-leader-workshop",
    ],
    faq: [
      {
        q: "Who is Isaac Rudansky?",
        a: "Isaac Rudansky is the founder of AdVenture Media, a New York performance advertising agency he started in 2012. He created one of Udemy's best-selling Google Ads courses, with more than 237,000 students, and now teaches AdVenture's live Claude workshops.",
      },
      {
        q: "What does Isaac Rudansky teach?",
        a: "Isaac teaches AdVenture's Claude Code for Beginners and Claude Cowork for Beginners workshops, co-hosts Claude for Design and The AI-First Leader, and has taught Google Ads, Meta advertising and landing page design on Udemy and CreativeLive.",
      },
      {
        q: "Can I book a private session with Isaac?",
        a: "Isaac's VIP 1:1 sessions are sold out right now. You can book a 90-minute VIP 1:1 with another AdVenture instructor on adventuremedia.ai/vip-session.",
      },
    ],
    sameAs: [
      "https://www.linkedin.com/in/isaacrudansky",
      "https://www.udemy.com/user/isaac-rudansky/",
      "https://www.creativelive.com/instructors/isaac-rudansky",
      "https://writersdigest.com/author/isaac-rudansky",
    ],
    brands: ["Forbes", "AMC Networks", "Hanes"],
    image: {
      lift: "/images/instructors/lift-isaac",
      headshot: "/images/headshots/isaac-rudansky.webp",
    },
    order: 1,
    // Esther, 2026-10-27: Isaac's VIP calendar is full.
    vipSoldOut: true,
  },

  // ─── Patrick Gilbert ────────────────────────────────────────────────────
  {
    slug: "patrick-gilbert",
    aside: "wrote the book on it",
    name: "Patrick Gilbert",
    firstName: "Patrick",
    alternateNames: ["Patrick James Gilbert"],
    jobTitle: "CEO",
    roleLine: "CEO, AdVenture Media",
    summary:
      "Patrick Gilbert is the CEO of AdVenture Media and the author of Join or Die: Digital Advertising in the Age of Automation and Never Always, Never Never: Strategic Marketing in an AI World. Google has invited him to speak to its Premier Partner agencies about automation, and he now teaches leaders how to run their companies with AI.",
    bio: [
      "Patrick joined AdVenture Media in 2015 as a digital strategist, after studying marketing and economics at Penn State and a first job as a marketing analyst on Wall Street. He became chief operating officer and, in January 2024, CEO.",
      "His subject for a decade has been what happens to advertising when machines take over the bidding. In 2016 he argued that manual bidding beat Google's automation. Within two years he had changed his mind in public, in a post titled Join or Die: Time to Embrace Google Automation. It traveled inside Google, and Google invited him to speak to its Premier Partner agencies, including a keynote at its Premier Partner Awards in Dublin. In 2022 he appeared in Google's own video series on how automation is changing agency work.",
      "He turned that argument into a book. Join or Die: Digital Advertising in the Age of Automation came out in 2020 with a foreword by David Sable, former global CEO of Young & Rubicam. His second book, Never Always, Never Never: Strategic Marketing in an AI World, followed in May 2026. Each of its 33 chapters links to an AI agent trained on the book, and he narrated the audiobook himself.",
      "PPC Hero named him to its Top 50 Most Influential PPC Experts in 2020 and its Top 25 in 2021.",
      "Inside AdVenture he has rebuilt a working agency around AI, down to requiring every employee to be proficient in Claude Code. He teaches what came out of that in The AI-First Leader workshop, which draws on the closing chapters of Never Always, Never Never, and co-teaches Claude Cowork for Beginners.",
    ],
    stats: [
      { value: "2", label: "Books on advertising and AI strategy" },
      { value: "4.43", label: "Goodreads rating for Join or Die" },
      { value: "Top 25", label: "PPC Hero's most influential experts, 2021" },
      { value: "CEO", label: "AdVenture Media since 2024" },
    ],
    timeline: [
      { year: "2015", text: "Joins AdVenture Media as a digital strategist." },
      { year: "2018", text: "Publishes Join or Die: Time to Embrace Google Automation, the post that reached Google." },
      { year: "2019", text: "Keynotes Google's Premier Partner Awards in Dublin." },
      { year: "2020", text: "Named to PPC Hero's Top 50. Publishes Join or Die: Digital Advertising in the Age of Automation." },
      { year: "2021", text: "Named to PPC Hero's Top 25 Most Influential PPC Experts." },
      { year: "2022", text: "Featured in Google's Agency Conversations video series on automation." },
      { year: "2024", text: "Becomes CEO of AdVenture Media." },
      { year: "2026", text: "Publishes Never Always, Never Never and launches The AI-First Leader workshop." },
    ],
    credentials: ["B.S., Marketing, Penn State University"],
    alumniOf: ["Penn State University"],
    knowsAbout: [
      "AI strategy",
      "Marketing strategy",
      "Advertising automation",
      "Smart Bidding",
      "Machine learning in advertising",
      "Google Ads",
      "Claude Cowork",
    ],
    works: [
      {
        kind: "book",
        title: "Never Always, Never Never: Strategic Marketing in an AI World",
        year: "2026",
        url: "https://neveralwaysbook.com/",
        cover: "/images/cowork-workshop/never-always-book-cover.webp",
        blurb:
          "Marketing strategy for the years when AI handles the execution, drawing on Ehrenberg-Bass, Byron Sharp and Binet and Field. Each of its 33 chapters links to an AI agent trained on the book, and Patrick narrates the audiobook himself.",
      },
      {
        kind: "book",
        title: "Join or Die: Digital Advertising in the Age of Automation",
        year: "2020",
        url: "https://www.joinordiebook.com/",
        blurb:
          "How machine learning took over Google and Facebook advertising, and what a marketer's job becomes once the platforms run the bidding. Foreword by David Sable, former global CEO of Young & Rubicam. Rated 4.43 on Goodreads.",
      },
      {
        kind: "podcast",
        title: "Velocity by Hawky, episode 17: Join or Die, Six Years On",
        year: "2026",
        url: "https://hawky.ai/podcast/patrick-gilbert-adventure-media",
      },
      {
        kind: "podcast",
        title: "Growth Manifesto: How AI is changing the marketing landscape",
        year: "2025",
        url: "https://www.growthmanifesto.com/podcast/patrick-gilbert",
      },
      {
        kind: "talk",
        title: "Affiliate World Dubai",
        year: "2023",
        url: "https://affiliateworldconferences.com/blog/speakers/patrick-gilbert/",
      },
      {
        kind: "article",
        title: "MediaPost: Google: How Agency Folks Really Feel About Automation",
        year: "2022",
        url: "https://www.mediapost.com/publications/article/379140/",
      },
    ],
    workshops: ["the-ai-first-leader-workshop", "claude-cowork-for-beginners-workshop"],
    faq: [
      {
        q: "Who is Patrick Gilbert?",
        a: "Patrick Gilbert is the CEO of AdVenture Media, a New York advertising agency, and the author of Join or Die: Digital Advertising in the Age of Automation (2020) and Never Always, Never Never: Strategic Marketing in an AI World (2026). He leads AdVenture's AI-First Leader workshop.",
      },
      {
        q: "What books has Patrick Gilbert written?",
        a: "Join or Die: Digital Advertising in the Age of Automation (2020), about how machine learning took over Google and Facebook advertising, and Never Always, Never Never: Strategic Marketing in an AI World (2026), about marketing strategy once AI handles the execution.",
      },
      {
        q: "Can I book a private session with Patrick?",
        a: "Yes. Book a 90-minute VIP 1:1 on adventuremedia.ai/vip-session and choose Patrick. You pay when you book, and Patrick emails you to set a time.",
      },
    ],
    sameAs: [
      "https://www.linkedin.com/in/patrickjamesgilbert",
      "https://x.com/PatrickJGilbert",
      "https://www.joinordiebook.com/",
      "https://neveralwaysbook.com/",
      "https://www.audible.com/author/Patrick-Gilbert/B004N2PSSK",
    ],
    image: {
      lift: "/images/instructors/lift-patrick",
      headshot: "/images/headshots/patrick-gilbert.webp",
    },
    order: 2,
  },

  // ─── Esther Nadoff ──────────────────────────────────────────────────────
  {
    slug: "esther-nadoff",
    aside: "design systems first",
    name: "Esther Nadoff",
    firstName: "Esther",
    alternateNames: ["Esti Nadoff"],
    // CONFIRM: canonical title. Eight variants exist; this is the one on
    // /workshops and in the Course schema. /about-us still says Director of Marketing.
    jobTitle: "Director of Education & Creative Strategy",
    roleLine: "Director of Education & Creative Strategy",
    summary:
      "Esther Nadoff leads education at AdVenture Media and the Modern Marketing Institute. She designed and teaches AdVenture's live Claude for Design workshop, which takes marketers and founders from a blank page to a finished, on-brand landing page in one morning.",
    bio: [
      "Esther came up through the agency side of AdVenture Media as a strategist on paid social and creative, writing the briefs, the ad copy and the head-to-head creative tests for brand accounts. That work turned into running creative direction across the team.",
      "In 2023 she helped launch AdVenture's course academy, the business that became the Modern Marketing Institute, and later directed its relaunch: the course videos, the certification guides and the look of the whole program. She also appears on camera with Isaac Rudansky in the institute's Paid Social Certification.",
      "In 2026 she and Isaac built the Claude for Design workshop together, and she leads it. She teaches design systems as the foundation of everything Claude makes: a single file of colors, type, grid and components that keeps a landing page, an email and an ad looking like the same brand. Her line for the goal is to bridge the gap between taste and tactical skill.",
      "She also wrote the AdVenture Design System 2026, the visual system behind this website and its workshop pages.",
    ],
    stats: [
      { value: "10+", label: "Claude for Design sessions taught in 2026" },
      { value: "2023", label: "Launched AdVenture's course academy" },
      { value: "4", label: "Claude tools she teaches: Chat, Cowork, Code, Design" },
    ],
    timeline: [
      { year: "2020", text: "Working on AdVenture client accounts as a strategist." },
      { year: "2022", text: "Leads creative strategy and ad copy for brand accounts." },
      { year: "2023", text: "Helps launch AdVenture's course academy." },
      { year: "2025", text: "Directs the Modern Marketing Institute relaunch and its certification content." },
      { year: "2026", text: "Builds and leads the Claude for Design workshop with Isaac Rudansky." },
    ],
    credentials: ["Thomas Edison State University"],
    alumniOf: ["Thomas Edison State University"],
    knowsAbout: [
      "Claude Design",
      "Design systems",
      "Landing page design",
      "Brand identity",
      "Creative strategy",
      "Paid social advertising",
      "AI for designers",
    ],
    works: [],
    workshops: ["claude-for-design-workshop", "claude-cowork-for-beginners-workshop"],
    quote: {
      text: "The same tool in the hands of different people will yield different results.",
      source: "Claude for Design workshop, August 2026",
    },
    faq: [
      {
        q: "Who is Esther Nadoff?",
        a: "Esther Nadoff, also known as Esti, is Director of Education and Creative Strategy at AdVenture Media and the Modern Marketing Institute. She designed and leads AdVenture's live Claude for Design workshop.",
      },
      {
        q: "What does Esther teach in the Claude for Design workshop?",
        a: "How to build a design system Claude can reuse, write prompts that set goal, layout, content and audience, use design vocabulary to steer the output, and take one brand from a landing page to email and ads. Attendees leave with a finished, deployed page.",
      },
      {
        q: "Can I book a private session with Esther?",
        a: "Yes. Book a 90-minute VIP 1:1 on adventuremedia.ai/vip-session and choose Esther. You pay when you book, and Esther emails you to set a time.",
      },
    ],
    // CONFIRM: this is her current profile.
    sameAs: ["https://www.linkedin.com/in/esther-nadoff-8ab878186/"],
    image: {
      lift: "/images/instructors/lift-esther",
      headshot: "/images/headshots/esther-nadoff.webp",
    },
    order: 3,
  },

  // ─── Nechama Teigman ────────────────────────────────────────────────────
  {
    slug: "nechama-teigman",
    aside: "builds our agents",
    name: "Nechama Teigman",
    firstName: "Nechama",
    // CONFIRM: vs "Director of AI Strategy" (newest string, Sep 2026).
    jobTitle: "Director of AI & Data Analytics",
    roleLine: "Director of AI & Data Analytics",
    summary:
      "Nechama Teigman is Director of AI and Data Analytics at AdVenture Media. A PPC Hero Rising Star known for custom attribution and Shapley-value models, she now builds the AI agents and internal tools AdVenture's team runs every day and leads the Advanced Claude Code workshop.",
    bio: [
      "Nechama joined AdVenture Media in 2017 as a data analyst on paid search and went on to lead strategy and analytics for the agency's largest clients, including publicly traded companies and firms with billion-dollar market caps.",
      "Her specialty is measurement: knowing which ads actually caused a sale. She builds custom attribution models for the question at hand, runs match-market incrementality tests, and has explained Shapley-value attribution on the PPC Ponderings podcast. In one published case she spent more than 100 hours building a model for a direct-to-consumer brand and found Facebook was reporting 590 more conversions than the brand had earned from it.",
      "PPC Hero named her one of its PPC Rising Stars of 2021. She is a winner of the Interpublic Group Marketing Award and studied marketing analytics at Baruch College.",
      "Today she builds AdVenture's AI infrastructure: the agents, MCP servers and internal tools the team works with every day. The Advanced Claude Code workshop she leads is built from what broke in those systems and how it got fixed.",
    ],
    stats: [
      { value: "2017", label: "Joined AdVenture Media" },
      { value: "2021", label: "PPC Hero Rising Star" },
      { value: "100+", label: "Hours on one custom attribution model" },
    ],
    timeline: [
      { year: "2017", text: "Joins AdVenture Media as a paid search data analyst." },
      { year: "2021", text: "Named a PPC Hero Rising Star. Leads strategy and analytics for top clients." },
      { year: "2022", text: "Guest on the PPC Ponderings podcast on attribution and Shapley models." },
      { year: "2022", text: "Publishes a case study on building custom attribution for a DTC brand." },
      { year: "2026", text: "Builds AdVenture's AI agents and leads the Advanced Claude Code workshop." },
    ],
    credentials: [
      "BBA, Marketing Analytics, Baruch College",
      "Interpublic Group Marketing Award",
      "PPC Hero Rising Star, 2021",
    ],
    alumniOf: ["Baruch College"],
    knowsAbout: [
      "Claude Code",
      "AI agents",
      "Model Context Protocol",
      "Marketing attribution",
      "Shapley value attribution",
      "Incrementality testing",
      "Marketing analytics",
    ],
    works: [
      {
        kind: "article",
        title: "Here's How We Bootstrapped a Custom Attribution Solution for a Unicorn DTC Brand",
        year: "2022",
        url: "https://adventuremedia.ai/blog/bootstrapping-a-custom-attribution-solution-for-a-dtc-brand",
      },
      {
        kind: "podcast",
        title: "PPC Ponderings: Shapley Models and Attributive Common Sense",
        year: "2022",
        url: "https://ppc.buzzsprout.com/1876974/episodes/9837766-bonus-nechama-teigman-on-shapley-models-attributive-common-sense",
      },
    ],
    // advanced-claude-code-workshop 404s on prod today (no published date),
    // so it is named in the bio but not linked. Add it back when it's live.
    workshops: ["claude-code-for-beginners-workshop"],
    testimonials: [
      {
        quote: "Ponder each and every word Nechama shares about attribution.",
        name: "Kirk Williams",
        role: "Host, PPC Ponderings",
      },
    ],
    faq: [
      {
        q: "Who is Nechama Teigman?",
        a: "Nechama Teigman is Director of AI and Data Analytics at AdVenture Media. She was named a PPC Hero Rising Star in 2021 for her attribution work, builds the agency's internal AI agents, and leads its Advanced Claude Code workshop.",
      },
      {
        q: "What is Nechama Teigman known for?",
        a: "Custom marketing attribution, including Shapley-value models and match-market incrementality tests, and building AI agents and MCP tools for a working agency.",
      },
      {
        q: "Can I book a private session with Nechama?",
        a: "Yes. Book a 90-minute VIP 1:1 on adventuremedia.ai/vip-session and choose Nechama. You pay when you book, and Nechama emails you to set a time.",
      },
    ],
    // CONFIRM: LinkedIn URL with her.
    sameAs: ["https://www.linkedin.com/in/nechama-teigman-003b30109/"],
    image: {
      lift: "/images/instructors/lift-nechama",
      headshot: "/images/headshots/nechama-teigman.webp",
    },
    order: 4,
  },

  // ─── Ronnie Cardno ──────────────────────────────────────────────────────
  {
    slug: "ronnie-cardno",
    aside: "sells with it daily",
    name: "Ronnie Cardno",
    firstName: "Ronnie",
    // CONFIRM: five titles in use; this matches the live Sales page.
    jobTitle: "AI Architect & Sales Executive",
    roleLine: "AI Architect & Sales Executive",
    summary:
      "Ronnie Cardno has sold for AdVenture Media since 2017. He built the Claude-powered sales system he runs on his own deals, covering prospect research, outreach and call recaps, and he wrote and leads AdVenture's Claude for Sales Teams workshop.",
    bio: [
      "Ronnie joined AdVenture Media in 2017 and worked his way from junior account manager to senior account manager to account executive before moving into new business. He studied communications and political science at Hofstra University.",
      "His sales system is built as reusable Claude skills. It starts with a profile of the seller: what they sell, who they sell to, how they qualify and how they write. On top of that sit three engines. Research turns a company name into a sourced brief, Outreach drafts in the seller's own voice, and Recap turns a call into notes, a deal status and the next email.",
      "He tested every piece on real deals before teaching it. Isaac Rudansky now works his own deals on the same system.",
      "In the workshop, attendees bring three of their own prospects and leave with a working system. His rule for the room: you're getting real Claude skills, using your own pipeline as the training ground.",
    ],
    stats: [
      { value: "2017", label: "Selling at AdVenture since" },
      { value: "3", label: "Engines in his Claude sales system" },
    ],
    timeline: [
      { year: "2017", text: "Joins AdVenture Media as a junior account manager." },
      { year: "2024", text: "Co-presents a talk on AI in ad platforms with Nechama Teigman at AdVenture's DOLAH conference." },
      { year: "2026", text: "Writes the Claude for Sales Teams curriculum and leads the workshop." },
    ],
    credentials: ["B.A., Communications and Political Science, Hofstra University"],
    alumniOf: ["Hofstra University"],
    knowsAbout: [
      "Claude for sales",
      "Sales prospecting",
      "Sales outreach",
      "Account management",
      "Claude skills",
      "B2B sales",
    ],
    works: [],
    workshops: ["claude-for-sales-teams-workshop"],
    quote: {
      text: "You're not getting prompt tips. You're getting real Claude skills, using your own pipeline as the training ground.",
      source: "Claude for Sales Teams curriculum, 2026",
    },
    testimonials: [
      {
        quote: "After one conversation with Ronnie, I was more than convinced.",
        name: "Burim Ahmedi",
        role: "Global Digital Marketing Manager, Curaden (review on Clutch)",
      },
    ],
    faq: [
      {
        q: "Who is Ronnie Cardno?",
        a: "Ronnie Cardno is a sales executive at AdVenture Media, where he has worked since 2017. He built the Claude-powered sales system the team uses for research, outreach and call recaps, and he leads AdVenture's Claude for Sales Teams workshop.",
      },
      {
        q: "What will I build in Ronnie's Claude for Sales workshop?",
        a: "A seller profile and three Claude engines: Research, which turns a company name into a sourced brief; Outreach, which drafts in your own voice; and Recap, which turns a call into notes and a next step. You build them on three of your own prospects.",
      },
      {
        q: "Can I book a private session with Ronnie?",
        a: "Yes. Book a 90-minute VIP 1:1 on adventuremedia.ai/vip-session and choose Ronnie. You pay when you book, and Ronnie emails you to set a time.",
      },
    ],
    // CONFIRM: LinkedIn URL. None on record.
    sameAs: [],
    image: {
      lift: "/images/instructors/lift-ronnie",
      headshot: "/images/headshots/ronnie-cardno.webp",
    },
    order: 5,
  },

  // ─── Brigid Healy ───────────────────────────────────────────────────────
  {
    slug: "brigid-healy",
    aside: "makes it sound like you",
    name: "Brigid Healy",
    firstName: "Brigid",
    // CONFIRM: six titles in use; this matches the live Sales page.
    jobTitle: "AI Architect & Sales Executive",
    roleLine: "AI Architect & Sales Executive",
    summary:
      "Brigid Healy is a sales executive at AdVenture Media, where she has worked on new business since 2022. She runs the agency's pipeline with Ronnie Cardno and co-teaches the Claude for Sales Teams workshop, where she works on attendees' outreach until it sounds like the person sending it.",
    bio: [
      "Brigid joined AdVenture Media in 2022. Her job starts before a client signs: she vets prospective clients for fit, then works with the account managers to set goals once they come on board.",
      "She knows the ad platforms from the inside. At DOLAH 2022, AdVenture's annual conference, she co-presented a testing framework for Google's Performance Max campaigns.",
      "Today she runs AdVenture's pipeline with Ronnie Cardno on the Claude system they use every day. In the Claude for Sales Teams workshop she leads the outreach and messaging work: she reads AI-drafted emails back to the room and the group rewrites them until they stop sounding like software. Nothing auto-sends.",
      "She also covers privacy, including the redaction habits she uses when she works on real deals with Claude.",
    ],
    stats: [
      { value: "2022", label: "Joined AdVenture Media" },
    ],
    timeline: [
      { year: "2022", text: "Joins AdVenture Media on new business." },
      { year: "2022", text: "Co-presents a Performance Max testing framework at AdVenture's DOLAH conference." },
      { year: "2026", text: "Co-teaches the Claude for Sales Teams workshop with Ronnie Cardno." },
    ],
    credentials: [],
    knowsAbout: [
      "Claude for sales",
      "Sales outreach",
      "Sales messaging",
      "Client qualification",
      "Performance Max",
      "B2B sales",
    ],
    works: [
      {
        kind: "article",
        title: "AdVenture Media Named as a Top Google Ads PPC Agency in 2024",
        year: "2024",
        url: "https://adventuremedia.ai/blog/adventure-media-named-as-a-top-google-ads-ppc-agency-in-2024",
      },
    ],
    workshops: ["claude-for-sales-teams-workshop"],
    faq: [
      {
        q: "Who is Brigid Healy?",
        a: "Brigid Healy is a sales executive at AdVenture Media, where she has worked on new business since 2022. She co-teaches AdVenture's Claude for Sales Teams workshop with Ronnie Cardno and leads its outreach and messaging work.",
      },
      {
        q: "What does Brigid teach?",
        a: "How to get AI-drafted sales outreach to sound like the person sending it, and how to work on real deals with Claude while keeping client data private.",
      },
      {
        q: "Can I book a private session with Brigid?",
        a: "Yes. Book a 90-minute VIP 1:1 on adventuremedia.ai/vip-session and choose Brigid. You pay when you book, and Brigid emails you to set a time.",
      },
    ],
    // CONFIRM: LinkedIn URL. None on record; high name-collision risk.
    sameAs: [],
    image: {
      lift: "/images/instructors/lift-brigid",
      headshot: "/images/headshots/brigid-healy.webp",
    },
    order: 6,
  },

  // ─── Dave Spector ───────────────────────────────────────────────────────
  {
    slug: "dave-spector",
    aside: "20 years behind the camera",
    name: "Dave Spector",
    firstName: "Dave",
    jobTitle: "Creative Director",
    roleLine: "Creative Director, AdVenture Media",
    summary:
      "Dave Spector is Creative Director at AdVenture Media, where he leads the agency's directors, editors and composers. Trained in film at NYU and at NYU's Interactive Telecommunications Program, he has spent more than 20 years in film and television and has been building generative AI into video production since 2022.",
    bio: [
      "Dave studied film and television at NYU and earned his master's at the university's Interactive Telecommunications Program, the graduate school where artists and engineers build new media together.",
      "He started his career making video for NewYork-Presbyterian and Loeb.nyc, and has taught at NYU and at Istanbul Bilgi University.",
      "At AdVenture Media he runs the creative department: directors, editors and composers who make the agency's video ads, with original music written in house. He has been putting generative AI to work in that production since 2022, using it for the parts it does well and keeping people on the parts that need taste.",
    ],
    stats: [
      { value: "20+", label: "Years in film and television" },
      { value: "500+", label: "Ads produced" },
      { value: "2022", label: "Building AI into production since" },
    ],
    timeline: [
      { year: "Early career", text: "Makes video at NewYork-Presbyterian and Loeb.nyc." },
      { year: "2022", text: "Starts building generative AI into AdVenture's video production." },
    ],
    credentials: [
      "BFA, Film and Television, New York University",
      "MPS, Interactive Telecommunications Program, New York University",
    ],
    alumniOf: ["New York University"],
    knowsAbout: [
      "Video production",
      "Generative AI video",
      "Creative direction",
      "Video advertising",
      "Film and television",
    ],
    works: [],
    workshops: [],
    faq: [
      {
        q: "Who is Dave Spector at AdVenture Media?",
        a: "Dave Spector is Creative Director at AdVenture Media, the New York advertising agency. He holds a BFA in film and television from NYU and a master's from NYU's Interactive Telecommunications Program, and leads AdVenture's video, editing and music team.",
      },
      {
        q: "Can I book a private session with Dave?",
        a: "Yes. Book a 90-minute VIP 1:1 on adventuremedia.ai/vip-session and choose Dave. You pay when you book, and Dave emails you to set a time.",
      },
    ],
    // CONFIRM: LinkedIn URL. Essential here: a well-known Japan-based TV
    // personality shares his name, and sameAs is how engines tell them apart.
    sameAs: [],
    image: {
      lift: "/images/instructors/lift-dave",
      headshot: "/assets/headshots/headshot_dave_spector.webp",
    },
    order: 7,
  },
  // ─── Spiros Maragkoudakis ───────────────────────────────────────────────
  {
    slug: "spiros-maragkoudakis",
    name: "Spiros Maragkoudakis",
    firstName: "Spiros",
    aside: "builds it with you",
    // CONFIRM: "Senior AI Architect" (our materials) vs "Chief Growth Officer"
    // (GrowthMentor). Also whether he is still at Globehunters; the bio leaves
    // that out until he confirms.
    jobTitle: "Senior AI Architect",
    roleLine: "Senior AI Architect, AdVenture Media",
    summary:
      "Spiros Maragkoudakis is Senior AI Architect at AdVenture Media, where he runs Brevio, the Claude Code Builder Program that coaches people from an idea to a working tool their team uses. He hosts the open Q&A hour after AdVenture's Claude Cowork workshop.",
    bio: [
      "Spiros spent more than a decade on the commercial side of travel and performance marketing before he started building with AI full time. He works from London.",
      "At AdVenture Media he runs Brevio, the Claude Code Builder Program. It takes one real tool for your business from idea to something your team runs on, step by step, with checkpoints along the way. Brevio is included with AdVenture Plus.",
      "He also keeps a running cheat sheet of Claude skills, tools and plugins, each one checked by hand before it goes out, and bundles the best of them into plugins you can install in Claude Code with one command.",
      "His own builds include a WhatsApp lead follow-up agent for a New York real-estate client that delivered 148 of 149 messages on its first send.",
      "He has mentored founders and marketers on GrowthMentor since 2021, with a 5.0 rating across 84 reviews. After every Claude Cowork workshop he stays on for open questions.",
    ],
    stats: [
      { value: "5.0", label: "GrowthMentor rating, 84 reviews" },
      { value: "97", label: "Mentoring sessions on GrowthMentor" },
      { value: "148/149", label: "Messages delivered by his WhatsApp lead agent" },
    ],
    timeline: [
      { year: "2021", text: "Starts mentoring founders and marketers on GrowthMentor." },
      { year: "2025", text: "Demos his first AI builds to the AdVenture team." },
      { year: "2026", text: "Launches Brevio, the Claude Code Builder Program, for AdVenture Plus members." },
    ],
    credentials: [],
    knowsAbout: ["Claude Code", "AI agents", "Claude skills and plugins", "Marketing automation", "Growth marketing"],
    works: [
      { kind: "project", title: "Brevio: the Claude Code Builder Program", year: "2026", url: "https://brevio.training" },
    ],
    workshops: ["claude-cowork-for-beginners-workshop"],
    faq: [
      {
        q: "Who is Spiros Maragkoudakis?",
        a: "Spiros Maragkoudakis is Senior AI Architect at AdVenture Media. He runs Brevio, the Claude Code Builder Program, and hosts the open Q&A after AdVenture's Claude Cowork workshop.",
      },
      {
        q: "Can I book a private session with Spiros?",
        a: "Yes. Book a 90-minute VIP 1:1 on adventuremedia.ai/vip-session and choose Spiros. You pay when you book, and Spiros emails you to set a time.",
      },
    ],
    sameAs: [
      "https://www.linkedin.com/in/spiros-maragkoudakis/",
      "https://github.com/spiros-ui",
      "https://www.growthmentor.com/mentors/spiros-maragkoudakis",
    ],
    image: {
      lift: "/images/instructors/lift-spiros",
      headshot: "/images/headshots/spiros-maragkoudakis.webp",
    },
    order: 8,
  },

];

export function getInstructor(slug: string): Instructor | undefined {
  return INSTRUCTORS.find((i) => i.slug === slug);
}

/** Display names for the series slugs instructors teach. */
export const WORKSHOP_NAMES: Record<string, string> = {
  "claude-code-for-beginners-workshop": "Claude Code for Beginners",
  "claude-cowork-for-beginners-workshop": "Claude Cowork for Beginners",
  "claude-for-design-workshop": "Claude for Design",
  "claude-for-sales-teams-workshop": "Claude for Sales Teams",
  "the-ai-first-leader-workshop": "The AI-First Leader",
  "advanced-claude-code-workshop": "Advanced Claude Code",
  "claude-code-for-kids": "Claude Code Camp for Kids",
};
